/**
 * Billing Configuration - Single Source of Truth for Plans and Pricing
 * 
 * This file centralizes all plan-related constants, price IDs, and feature mappings.
 * Update this file when plans or pricing change.
 */

export type PlanId = 'launch' | 'boost' | 'scale';
export type BillingInterval = 'month' | 'year';

export interface PlanConfig {
  id: PlanId;
  displayName: string;
  description: string;
  features: string[];
  limits: {
    maxClients: number; // -1 for unlimited
    maxOrders: number; // -1 for unlimited
    maxProjects: number; // -1 for unlimited
    maxStorage: number; // GB, -1 for unlimited
    maxTeamMembers: number; // -1 for unlimited
  };
  permissions: {
    dashboard: boolean;
    clients: boolean;
    orders: boolean;
    calendar: boolean;
    referrals: boolean;
    workboard: boolean;
    stats: boolean;
    invoices: boolean;
    assistant: boolean; // AI Assistant - Scale only
    admin: boolean; // Admin access (not a plan feature)
  };
  stripe: {
    monthly: {
      productId: string;
      priceId: string;
      amount: number; // in cents
    };
    yearly: {
      productId: string;
      priceId: string;
      amount: number; // in cents
    };
  };
}

/**
 * Plan configurations
 * Update Stripe price IDs here when they change
 */
export const PLANS: Record<PlanId, PlanConfig> = {
  launch: {
    id: 'launch',
    displayName: 'Launch',
    description: 'Perfect for getting started',
    features: [
      'Up to 5 clients',
      'Up to 10 orders per month',
      'Basic dashboard',
      'Client management',
      'Order tracking',
    ],
    limits: {
      maxClients: 5,
      maxOrders: 10,
      maxProjects: 1,
      maxStorage: 1,
      maxTeamMembers: 1,
    },
    permissions: {
      dashboard: true,
      clients: true,
      orders: true,
      calendar: false,
      referrals: false,
      workboard: false,
      stats: false,
      invoices: false,
      assistant: false,
      admin: false,
    },
    stripe: {
      monthly: {
        productId: 'prod_free', // Free plan - no Stripe product
        priceId: 'price_free',
        amount: 0,
      },
      yearly: {
        productId: 'prod_free',
        priceId: 'price_free',
        amount: 0,
      },
    },
  },
  boost: {
    id: 'boost',
    displayName: 'Boost',
    description: 'For growing freelancers',
    features: [
      'Unlimited clients',
      'Unlimited orders',
      'Calendar integration',
      'Task management',
      'Referral program',
      'All Launch features',
    ],
    limits: {
      maxClients: -1,
      maxOrders: -1,
      maxProjects: 5,
      maxStorage: 10,
      maxTeamMembers: 1,
    },
    permissions: {
      dashboard: true,
      clients: true,
      orders: true,
      calendar: true,
      referrals: true,
      workboard: true,
      stats: false,
      invoices: false,
      assistant: false,
      admin: false,
    },
    stripe: {
      monthly: {
        productId: 'prod_TNy8jOMBKSwldP',
        priceId: 'price_1SRCBnENcVsHr4WIuic9CJOK',
        amount: 2400, // $24.00
      },
      yearly: {
        productId: 'prod_TNy8jOMBKSwldP',
        priceId: 'price_1SRCBnENcVsHr4WIuic9CJOK',
        amount: 24000, // $240.00
      },
    },
  },
  scale: {
    id: 'scale',
    displayName: 'Scale',
    description: 'For power users and teams',
    features: [
      'Everything in Boost',
      'Advanced statistics',
      'Invoice system',
      'AI Assistant',
      'Team collaboration',
      'Priority support',
    ],
    limits: {
      maxClients: -1,
      maxOrders: -1,
      maxProjects: 15,
      maxStorage: 50,
      maxTeamMembers: 5,
    },
    permissions: {
      dashboard: true,
      clients: true,
      orders: true,
      calendar: true,
      referrals: true,
      workboard: true,
      stats: true,
      invoices: true,
      assistant: true, // Scale-only feature
      admin: false,
    },
    stripe: {
      monthly: {
        productId: 'prod_TNyAA6s2Ni4yPK',
        priceId: 'price_1SRCDrENcVsHr4WI7BwPORhF',
        amount: 3900, // $39.00
      },
      yearly: {
        productId: 'prod_TNyAA6s2Ni4yPK',
        priceId: 'price_1SRCDrENcVsHr4WI7BwPORhF',
        amount: 39000, // $390.00
      },
    },
  },
};

/**
 * Get plan configuration by ID
 */
export const getPlan = (planId: PlanId | string): PlanConfig | null => {
  const normalized = planId.toLowerCase() as PlanId;
  return PLANS[normalized] || null;
};

/**
 * Get plan by Stripe price ID
 */
export const getPlanByPriceId = (priceId: string): PlanConfig | null => {
  for (const plan of Object.values(PLANS)) {
    if (plan.stripe.monthly.priceId === priceId || plan.stripe.yearly.priceId === priceId) {
      return plan;
    }
  }
  return null;
};

/**
 * Get plan by Stripe product ID
 */
export const getPlanByProductId = (productId: string): PlanConfig | null => {
  for (const plan of Object.values(PLANS)) {
    if (plan.stripe.monthly.productId === productId || plan.stripe.yearly.productId === productId) {
      return plan;
    }
  }
  return null;
};

/**
 * Check if a plan has access to a feature
 */
export const hasFeature = (planId: PlanId | string, feature: keyof PlanConfig['permissions']): boolean => {
  const plan = getPlan(planId);
  if (!plan) return false;
  return plan.permissions[feature] || false;
};

/**
 * Get plan limits
 */
export const getPlanLimits = (planId: PlanId | string): PlanConfig['limits'] => {
  const plan = getPlan(planId);
  if (!plan) {
    // Return Launch limits as default
    return PLANS.launch.limits;
  }
  return plan.limits;
};

/**
 * Calculate yearly savings percentage
 */
export const getYearlySavings = (planId: PlanId): number => {
  const plan = PLANS[planId];
  if (!plan) return 0;
  
  const monthlyCost = plan.stripe.monthly.amount * 12;
  const yearlyCost = plan.stripe.yearly.amount;
  const savings = monthlyCost - yearlyCost;
  
  return Math.round((savings / monthlyCost) * 100);
};

/**
 * Format price for display
 */
export const formatPrice = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

/**
 * Get all plan IDs in order
 */
export const getPlanIds = (): PlanId[] => {
  return ['launch', 'boost', 'scale'];
};

/**
 * Get next plan (for upgrade suggestions)
 */
export const getNextPlan = (currentPlan: PlanId | string): PlanConfig | null => {
  const planIds = getPlanIds();
  const currentIndex = planIds.indexOf(currentPlan.toLowerCase() as PlanId);
  if (currentIndex === -1 || currentIndex === planIds.length - 1) {
    return null;
  }
  return PLANS[planIds[currentIndex + 1]];
};

